import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "@/layout/Sidebar";
import TopNavbar from "@/layout/TopNavbar";
import usePostData from "@/hooks/usePostData";
import useGetQuery from "@/hooks/useGetQuery";
import apiClient from "@/services/apiClient";
import useLlmPlatformFilter from "@/hooks/useLlmPlatformFilter";
import {
  getLocalStorageWithExpiry,
  showApiError,
  showSuccess,
  showWarning,
} from "@/utils";
import { CLIENT_ID_KEY } from "@/const";
import NColumnTable from "@/charts/NColumnTable";
import {
  SquareArrowOutUpRight,
  Trash2,
  Plus,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import TrackingChart from "@/charts/TrackingChart";
import CustomDropdown from "@/components/ui/CustomDropdown";
import Spinner from "@/components/ui/Spinner";
import { BACKEND_URL } from "@/config";
import BrandButton from "@/components/ui/BrandButton";
import SectionHeading from "@/components/ui/SectionHeading";
import "@/pages/page.css";


// Utility function to clean URL for display
const cleanUrlForDisplay = (url) => {
  try {
    // Parse the URL to get the hostname
    const urlObj = new URL(url);
    let hostname = urlObj.hostname;

    // Remove 'www.' and anything before it
    if (hostname.includes("www.")) {
      hostname = hostname.substring(hostname.indexOf("www.") + 4);
    }

    // Add the path if it exists and is not just "/"
    const path = urlObj.pathname !== "/" ? urlObj.pathname : "";

    return hostname + path;
  } catch (e) {
    // If URL parsing fails, return the original
    return url;
  }
};

function TrackingPage() {
  const { t, i18n } = useTranslation();
  const checkpoint = 6;
  const client_id = getLocalStorageWithExpiry(CLIENT_ID_KEY);

  // Search engine filter, limited to the client's pricing tier
  const {
    llmPlatforms: LLM_Platforms,
    setLlmPlatforms,
    engineOptions,
    isPending: isLlmProvidersPending,
  } = useLlmPlatformFilter(client_id);
  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(true);
  const [brandFilter, setBrandFilter] = useState("all"); // "all" | "branded" | "unbranded"
  const [funnelStageFilter, setFunnelStageFilter] = useState("all"); // "all" | "top" | "middle" | "bottom"
  const [deletingUrl, setDeletingUrl] = useState(null);
  const [deletingQuery, setDeletingQuery] = useState(null);
  const TrackingWeekMinCount = 3; // Minimum number of weeks with data to show tracking chart

  const isBrandedParam =
    brandFilter === "branded"
      ? true
      : brandFilter === "unbranded"
        ? false
        : undefined;

  const funnelStageParam =
    funnelStageFilter === "all" ? undefined : funnelStageFilter;

  // Modal states
  const [showAddUrlModal, setShowAddUrlModal] = useState(false);
  const [showAddQueryModal, setShowAddQueryModal] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newQuery, setNewQuery] = useState("");
  const [newUrlDateTracked, setNewUrlDateTracked] = useState(""); // New state for URL date
  const [newQueryDateTracked, setNewQueryDateTracked] = useState(""); // New state for Query date
  const [isSubmittingUrl, setIsSubmittingUrl] = useState(false);
  const [isSubmittingQuery, setIsSubmittingQuery] = useState(false);

  // Historical queries states
  const [historicalQueries, setHistoricalQueries] = useState([]);
  const [addingHistoricalQuery, setAddingHistoricalQuery] = useState(null);

  const toggleSidebar = () => {
    setIsPermanentlyExpanded(!isPermanentlyExpanded);
  };

  const runAssistantResearch = (queryValue) => {
    if (!queryValue || typeof window.triggerElelemAssistantPrompt !== "function") return;
    const isJa = (i18n.resolvedLanguage || i18n.language || "").startsWith("ja");
    const prompt = isJa
      ? `キーワード「${queryValue}」に関するプロンプト分析とリアルタイムリサーチを実行してください。`
      : `Run real-time prompt research on keyword "${queryValue}".`;

    window.triggerElelemAssistantPrompt(prompt);
  };

  // Loading data from backend
  const [
    TrackedUrlResult,
    TrackedUrlIsLoaded,
    TrackedUrlError,
    refetchTrackedUrls,
  ] = usePostData(
    BACKEND_URL + "/api/v1/tracking-page/tracked-urls",
    {
      checkpoint: checkpoint,
      llm_platforms: LLM_Platforms,
      ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
      ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
    },
    { skip: isLlmProvidersPending },
  );

  const [
    TrackedCitationsResult,
    TrackedCitationsIsLoaded,
    TrackedCitationsError,
    refetchTrackedCitations,
  ] = usePostData(
    BACKEND_URL + "/api/v1/tracking-page/tracked-citations",
    {
      checkpoint: checkpoint,
      llm_platforms: LLM_Platforms,
      ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
      ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
    },
    { skip: isLlmProvidersPending },
  );

  const [
    TrackedMentionsResult,
    TrackedMentionsIsLoaded,
    TrackedMentionsError,
    refetchTrackedMentions,
  ] = usePostData(
    BACKEND_URL + "/api/v1/tracking-page/tracked-mentions",
    {
      checkpoint: checkpoint,
      llm_platforms: LLM_Platforms,
      ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
      ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
    },
    { skip: isLlmProvidersPending },
  );

  const [SuggestedUrlsResult, SuggestedUrlsIsLoaded] = useGetQuery(
    BACKEND_URL + "/api/v1/tracking-page/suggested-urls",
  );

  const [SuggestedQueriesResult, SuggestedQueriesIsLoaded] = useGetQuery(
    BACKEND_URL + "/api/v1/tracking-page/suggested-queries",
  );

  // Fetch historical queries and filter out already tracked ones
  useEffect(() => {
    // Only fetch if TrackedCitationsResult has been loaded and is not in a loading state
    if (!TrackedCitationsResult || !TrackedCitationsIsLoaded) {
      return;
    }

    const fetchHistoricalQueries = async () => {
      try {
        // Fetch query research history
        const historyResponse = await apiClient.get(
          `${BACKEND_URL}/api/v1/query-research/history?limit=50`,
        );
        const historyData = historyResponse.data;

        if (historyData && historyData.success) {
          // Extract tracked queries from Citations only (since Citations and Mentions have the same queries)
          const trackedQuerySet = new Set();

          if (TrackedCitationsResult?.tracking_result) {
            const citationQueryColumn =
              TrackedCitationsResult.column_names?.[0];
            TrackedCitationsResult.tracking_result.forEach((item) => {
              if (citationQueryColumn && item[citationQueryColumn]) {
                trackedQuerySet.add(item[citationQueryColumn]);
              }
            });
          }

          // Extract queries from history array and filter out already tracked ones
          const filteredQueries = historyData.history.filter(
            (query) => !trackedQuerySet.has(query),
          );

          setHistoricalQueries(filteredQueries);
        }
      } catch (error) {
        console.error("Error fetching historical queries:", error);
      }
    };

    fetchHistoricalQueries();
  }, [TrackedCitationsResult, TrackedCitationsIsLoaded]);

  // Handler for adding historical query to tracked queries
  const handleAddHistoricalQuery = async (query) => {
    setAddingHistoricalQuery(query);

    try {
      const response = await apiClient.post(
        `${BACKEND_URL}/api/v1/tracking-page/add-query?query=${encodeURIComponent(query)}`,
      );

      const data = response.data;

      if (data && data.success) {
        // Remove from historical queries list
        setHistoricalQueries((prev) => prev.filter((q) => q !== query));
        refetchTrackedCitations();
        refetchTrackedMentions();
      } else {
        showApiError(data?.message || t("tracking.addPromptError", "Failed to add prompt. Please try again."));
      }
    } catch (error) {
      console.error("Error adding historical query:", error);
      showApiError(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          t("tracking.addPromptError", "Failed to add prompt. Please try again."),
      );
    } finally {
      setAddingHistoricalQuery(null);
    }
  };

  // Check if data is empty
  const hasUrlData = TrackedUrlResult?.tracking_result?.length > 0;
  const hasCitationData = TrackedCitationsResult?.tracking_result?.length > 0;
  const hasMentionData = TrackedMentionsResult?.tracking_result?.length > 0;
  const hasQueryData = hasCitationData || hasMentionData;

  // Extract date columns and calculate valid week counts
  const urlDateColumns = TrackedUrlResult?.column_names?.slice(1) || [];
  const citationDateColumns =
    TrackedCitationsResult?.column_names?.slice(1) || [];
  const mentionDateColumns =
    TrackedMentionsResult?.column_names?.slice(1) || [];

  // Calculate valid week count for URL tracking
  const url_valid_week_count = urlDateColumns.filter((dateCol) => {
    return TrackedUrlResult?.tracking_result?.some((row) => {
      const value = row[dateCol];
      return (
        value !== "-" &&
        value !== null &&
        value !== undefined &&
        value !== "" &&
        value !== 0 &&
        value !== "0"
      );
    });
  }).length;

  // Calculate valid week count for Citation tracking
  const citation_valid_week_count = citationDateColumns.filter((dateCol) => {
    return TrackedCitationsResult?.tracking_result?.some((row) => {
      const value = row[dateCol];
      return (
        value !== "-" &&
        value !== null &&
        value !== undefined &&
        value !== "" &&
        value !== 0 &&
        value !== "0"
      );
    });
  }).length;

  // Calculate valid week count for Mention tracking
  const mention_valid_week_count = mentionDateColumns.filter((dateCol) => {
    return TrackedMentionsResult?.tracking_result?.some((row) => {
      const value = row[dateCol];
      return (
        value !== "-" &&
        value !== null &&
        value !== undefined &&
        value !== "" &&
        value !== 0 &&
        value !== "0"
      );
    });
  }).length;

  const engineAllOption = {
    value: "all",
    label: t("dashboard.allSearchApps", "All GenAI Search Apps"),
  };

  // Add URL handler - Updated to include date_tracked
  const handleAddUrl = async (e) => {
    e.preventDefault();

    if (!newUrl.trim()) {
      showWarning(t("tracking.enterUrlWarning", "Please enter a URL"));
      return;
    }

    setIsSubmittingUrl(true);

    try {
      // Build URL with optional date_tracked parameter
      let apiUrl = `${BACKEND_URL}/api/v1/tracking-page/add-url?url=${encodeURIComponent(newUrl.trim())}`;

      if (newUrlDateTracked) {
        // Convert date to ISO format if provided
        const dateObj = new Date(newUrlDateTracked);
        const isoDate = dateObj.toISOString();
        apiUrl += `&targeted_date=${encodeURIComponent(isoDate)}`;
      }

      const response = await apiClient.post(apiUrl);
      const data = response.data;

      if (data && data.success) {
        showSuccess(
          data.message ||
            t(
              "tracking.urlAddedSuccess",
              "URL added to tracked URLs successfully.",
            ),
        );
        setNewUrl("");
        setNewUrlDateTracked("");
        setShowAddUrlModal(false);
        refetchTrackedUrls();
      } else {
        showApiError(
          data?.message ||
            t("tracking.addUrlError", "Failed to add URL. Please try again."),
        );
      }
    } catch (error) {
      console.error("Error adding URL:", error);
      showApiError(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          t("tracking.addUrlError", "Failed to add URL. Please try again."),
      );
    } finally {
      setIsSubmittingUrl(false);
    }
  };

  // Add Query handler - Updated to include date_tracked
  const handleAddQuery = async (e) => {
    e.preventDefault();

    if (!newQuery.trim()) {
      showWarning(t("tracking.enterPromptWarning", "Please enter a prompt"));
      return;
    }

    setIsSubmittingQuery(true);

    try {
      // Build URL with optional date_tracked parameter
      let apiUrl = `${BACKEND_URL}/api/v1/tracking-page/add-query?query=${encodeURIComponent(newQuery.trim())}`;

      if (newQueryDateTracked) {
        // Convert date to ISO format if provided
        const dateObj = new Date(newQueryDateTracked);
        const isoDate = dateObj.toISOString();
        apiUrl += `&targeted_date=${encodeURIComponent(isoDate)}`;
      }

      const response = await apiClient.post(apiUrl);
      const data = response.data;

      if (data && data.success) {
        showSuccess(
          data.message ||
            t(
              "tracking.keywordAddedSuccess",
              "Keyword added to tracked keywords successfully.",
            ),
        );
        setNewQuery("");
        setNewQueryDateTracked("");
        setShowAddQueryModal(false);
        refetchTrackedCitations();
        refetchTrackedMentions();
      } else {
        showApiError(
          data?.message ||
            t(
              "tracking.addPromptError",
              "Failed to add prompt. Please try again.",
            ),
        );
      }
    } catch (error) {
      console.error("Error adding query:", error);
      showApiError(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          t("tracking.addPromptError", "Failed to add prompt. Please try again."),
      );
    } finally {
      setIsSubmittingQuery(false);
    }
  };

  // Delete URL handler
  const handleDeleteUrl = async (url) => {
    if (
      !window.confirm(
        t(
          "tracking.confirmDeleteUrl",
          `Are you sure you want to delete tracking for this URL?\n\n${url}`,
          { url },
        ),
      )
    ) {
      return;
    }

    setDeletingUrl(url);

    try {
      await apiClient.delete(
        `${BACKEND_URL}/api/v1/tracking-page/delete-url?url=${encodeURIComponent(url)}`,
      );

      refetchTrackedUrls();
    } catch (error) {
      console.error("Error deleting URL:", error);
      showApiError(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          t("tracking.deleteUrlError", "Failed to delete URL. Please try again."),
      );
    } finally {
      setDeletingUrl(null);
    }
  };

  // Delete Query handler
  const handleDeleteQuery = async (query) => {
    if (
      !window.confirm(
        t(
          "tracking.confirmDeletePrompt",
          `Are you sure you want to delete tracking for this prompt?\n\n${query}`,
          { query },
        ),
      )
    ) {
      return;
    }

    setDeletingQuery(query);

    try {
      await apiClient.delete(
        `${BACKEND_URL}/api/v1/tracking-page/delete-query?query=${encodeURIComponent(query)}`,
      );

      refetchTrackedCitations();
      refetchTrackedMentions();
    } catch (error) {
      console.error("Error deleting query:", error);
      showApiError(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          t(
            "tracking.deletePromptError",
            "Failed to delete prompt. Please try again.",
          ),
      );
    } finally {
      setDeletingQuery(null);
    }
  };

  // Process URL data to add actions column and clean URL display
  const processedUrlData =
    TrackedUrlResult?.tracking_result?.map((item) => {
      const urlColumnName = TrackedUrlResult.column_names?.[0];
      return {
        ...item,
        // Clean the URL for display
        [urlColumnName]: cleanUrlForDisplay(item[urlColumnName]),
        actions: (
          <div className="d-flex gap-2 justify-content-center">
            <button
              className="btn btn-sm btn-outline-primary p-1"
              style={{ width: "28px", height: "28px" }}
              onClick={() => window.open(item.url, "_blank")}
              title={t("tracking.openUrl", "Open URL in new tab")}
            >
              <SquareArrowOutUpRight size={14} />
            </button>
            <button
              className="btn btn-sm btn-outline-danger p-1"
              style={{ width: "28px", height: "28px" }}
              onClick={() => handleDeleteUrl(item.url)}
              disabled={deletingUrl === item.url}
              title={t("tracking.deleteUrl", "Delete URL tracking")}
            >
              {deletingUrl === item.url ? (
                <span
                  className="spinner-border spinner-border-sm"
                  style={{ width: "12px", height: "12px" }}
                ></span>
              ) : (
                <Trash2 size={14} />
              )}
            </button>
          </div>
        ),
        preview: <div>{item.url}</div>,
      };
    }) || [];

  // Process Citation data to add actions column
  const processedCitationData =
    TrackedCitationsResult?.tracking_result?.map((item) => {
      const firstColumnKey = TrackedCitationsResult.column_names?.[0];
      const queryValue = firstColumnKey ? item[firstColumnKey] : null;

      return {
        ...item,
        actions: (
          <div className="d-flex gap-2 justify-content-center">
            <button
              type="button"
              className="tracking-assistant-btn"
              onClick={() => {
                runAssistantResearch(queryValue);
              }}
              title={t("tracking.runAssistantResearch", "Run elelem Assistant research")}
              aria-label={`Run elelem Assistant research for ${queryValue || "this keyword"}`}
            >
              <Sparkles size={14} aria-hidden="true" />
            </button>
            <button
              className="btn btn-sm btn-outline-danger p-1"
              style={{ width: "28px", height: "28px" }}
              onClick={() => handleDeleteQuery(queryValue)}
              disabled={deletingQuery === queryValue}
              title={t("tracking.deletePromptTracking", "Delete prompt tracking")}
            >
              {deletingQuery === queryValue ? (
                <span
                  className="spinner-border spinner-border-sm"
                  style={{ width: "12px", height: "12px" }}
                ></span>
              ) : (
                <Trash2 size={14} />
              )}
            </button>
          </div>
        ),
      };
    }) || [];

  // Process Mention data to add actions column
  const processedMentionData =
    TrackedMentionsResult?.tracking_result?.map((item) => {
      const firstColumnKey = TrackedMentionsResult.column_names?.[0];
      const queryValue = firstColumnKey ? item[firstColumnKey] : null;

      return {
        ...item,
        actions: (
          <div className="d-flex gap-2 justify-content-center">
            <button
              type="button"
              className="tracking-assistant-btn"
              onClick={() => {
                runAssistantResearch(queryValue);
              }}
              title={t("tracking.runAssistantResearch", "Run elelem Assistant research")}
              aria-label={`Run elelem Assistant research for ${queryValue || "this keyword"}`}
            >
              <Sparkles size={14} aria-hidden="true" />
            </button>
            <button
              className="btn btn-sm btn-outline-danger p-1"
              style={{ width: "28px", height: "28px" }}
              onClick={() => handleDeleteQuery(queryValue)}
              disabled={deletingQuery === queryValue}
              title={t("tracking.deletePromptTracking", "Delete prompt tracking")}
            >
              {deletingQuery === queryValue ? (
                <span
                  className="spinner-border spinner-border-sm"
                  style={{ width: "12px", height: "12px" }}
                ></span>
              ) : (
                <Trash2 size={14} />
              )}
            </button>
          </div>
        ),
      };
    }) || [];

  // Get column keys and titles from API response
  const urlColumnKeys = TrackedUrlResult?.column_names
    ? [
        TrackedUrlResult.column_names[0],
        "actions",
        ...TrackedUrlResult.column_names.slice(1),
      ]
    : [];
  const urlColumnTitles = TrackedUrlResult?.column_names
    ? [
        t("common.url", "URL"),
        "",
        ...TrackedUrlResult.column_names.slice(1),
      ]
    : [];

  const citationColumnKeys = TrackedCitationsResult?.column_names
    ? [
        TrackedCitationsResult.column_names[0],
        "actions",
        ...TrackedCitationsResult.column_names.slice(1),
      ]
    : [];
  const citationColumnTitles = TrackedCitationsResult?.column_names
    ? [
        t("tracking.prompts", "Prompts"),
        "",
        ...TrackedCitationsResult.column_names.slice(1),
      ]
    : [];

  const mentionColumnKeys = TrackedMentionsResult?.column_names
    ? [
        TrackedMentionsResult.column_names[0],
        "actions",
        ...TrackedMentionsResult.column_names.slice(1),
      ]
    : [];
  const mentionColumnTitles = TrackedMentionsResult?.column_names
    ? [
        t("tracking.prompts", "Prompts"),
        "",
        ...TrackedMentionsResult.column_names.slice(1),
      ]
    : [];

  return (
    <div className="app-container">
      <Sidebar
        currentPage={"tracking"}
        isPermanentlyExpanded={isPermanentlyExpanded}
      />
      <main className="content bg-page">
        <div className="container-fluid p-4">
          <TopNavbar
            isPermanentlyExpanded={isPermanentlyExpanded}
            toggleSidebar={toggleSidebar}
          />

          {/* Heading  */}
          <h3 className="h4 fw-bold text-dark">{t("tracking.title", "Tracking")}</h3>
          <p className="mt-1 fs-6 text-secondary">
            {t(
              "tracking.subtitle",
              "Monitor share of citations improvements for prompts and pages over time",
            )}
          </p>

          {/* Filter Area */}
          <div
            className="card card-radius border-1 shadow-sm mb-4"
            style={{ borderColor: "var(--border)" }}
            data-tour="tracking.filters"
          >
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-12 col-sm-auto mb-3 mb-sm-0">
                  <div className="d-flex align-items-center">
                    <i
                      className="bi bi-funnel text-muted me-2"
                      style={{ fontSize: "16px" }}
                    ></i>
                    <h6 className="mb-0 text-muted fw-medium">
                      {t("tracking.filterTitle", "Filter Tracking Data")}
                    </h6>
                  </div>
                </div>

                <div className="col-12 col-sm">
                  <div className="row g-3 justify-content-end">
                    <div className="col-12 col-md-auto">
                      <CustomDropdown
                        multiple
                        allOption={engineAllOption}
                        placeholder={t(
                          "dashboard.selectSearchEngines",
                          "Select search engines",
                        )}
                        selectAllLabel={t("common.selectAll", "Select All")}
                        deselectAllLabel={t("common.deselectAll", "Deselect All")}
                        doneLabel={t("common.done", "Done")}
                        options={engineOptions}
                        value={LLM_Platforms}
                        onChange={setLlmPlatforms}
                        style={{ minWidth: "180px" }}
                      />
                    </div>

                    <div className="col-12 col-md-auto">
                      <CustomDropdown
                        options={[
                          { value: "all", label: t("common.all", "All") },
                          { value: "branded", label: t("common.yes", "Yes") },
                          { value: "unbranded", label: t("common.no", "No") },
                        ]}
                        value={brandFilter}
                        onChange={setBrandFilter}
                        label={t("dashboard.isBranded", "Is branded")}
                        style={{ minWidth: "150px" }}
                      />
                    </div>

                    <div className="col-12 col-md-auto">
                      <CustomDropdown
                        options={[
                          { value: "all", label: t("common.all", "All") },
                          {
                            value: "top",
                            label: t("tracking.topFunnel", "Top"),
                          },
                          {
                            value: "middle",
                            label: t("tracking.middleFunnel", "Middle"),
                          },
                          {
                            value: "bottom",
                            label: t("tracking.bottomFunnel", "Bottom"),
                          },
                        ]}
                        value={funnelStageFilter}
                        onChange={setFunnelStageFilter}
                        label={t("tracking.funnelStage", "Funnel stage")}
                        style={{ minWidth: "150px" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pages Section */}
          <div className="row g-4 align-items-stretch">
            <div className="d-flex justify-content-between align-items-center mb-2" data-tour="tracking.pages">
              <SectionHeading>{t("tracking.pages", "Pages")}</SectionHeading>
              <BrandButton
                onClick={() => setShowAddUrlModal(true)}
                style={{ fontSize: "0.875rem", gap: "0.5rem" }}
              >
                <Plus size={16} />
                {t("tracking.addPage", "Add Page")}
              </BrandButton>
            </div>
            {/* Display Loading spinner */}
            {!TrackedUrlIsLoaded && (
              <div className="col-12 d-flex justify-content-center my-5">
                <Spinner />
              </div>
            )}

            {/* Warning for No URL Data */}
            {TrackedUrlIsLoaded && !hasUrlData && (
              <div className="col-12">
                <div
                  className="alert alert-warning d-flex align-items-center"
                  role="alert"
                >
                  <AlertCircle size={20} className="me-3 flex-shrink-0" />
                  <div className="flex-grow-1">
                    <strong>{t("tracking.noPagesDataTitle", "No Pages Data Available.")}</strong>{" "}
                    {t(
                      "tracking.noPagesDataDesc",
                      "Add a page URL to start tracking, or change the selected engine using the dropdown in the Filter Dashboard above. You can also click the \"Add Page\" button above to add a URL and begin tracking.",
                    )}
                  </div>
                </div>
              </div>
            )}

            {hasUrlData && (
              <div className="col-md-6 d-flex flex-column" data-tour="tracking.url-table">
                <NColumnTable
                  data={processedUrlData}
                  n={8}
                  columnKeys={urlColumnKeys}
                  columnTitles={urlColumnTitles}
                  columnRatio="32-8-10-10-10-10-10-10"
                  columnAlignment="l-r-r-r-r-r-r-r"
                  title={t("tracking.trackedUrls", "Tracked URLs")}
                  isLoaded={TrackedUrlIsLoaded}
                  error={TrackedUrlError}
                  boxHeight="300px"
                  previewColumn={"preview"}
                  className="h-100"
                />
              </div>
            )}

            {url_valid_week_count >= TrackingWeekMinCount && (
              <div className="col-md-6 d-flex flex-column">
                <TrackingChart
                  data={TrackedUrlResult?.tracking_result || []}
                  title={t("tracking.urlTrends", "URL Trends")}
                  queryColumn={TrackedUrlResult?.column_names?.[0] || "url"}
                  dateColumns={TrackedUrlResult?.column_names?.slice(1) || []}
                  additionalInfo={TrackedUrlResult?.additional_info || null}
                  isLoaded={TrackedUrlIsLoaded}
                  error={TrackedUrlError}
                  height={330}
                  className="h-100"
                />
              </div>
            )}
          </div>

          {/* Queries Section */}
          <div className="row g-4 mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2" data-tour="tracking.prompts">
              <SectionHeading>{t("tracking.prompts", "Prompts")}</SectionHeading>
              <BrandButton
                onClick={() => setShowAddQueryModal(true)}
                style={{ fontSize: "0.875rem", gap: "0.5rem" }}
              >
                <Plus size={16} />
                {t("promptLibrary.addPrompt", "Add Prompt")}
              </BrandButton>
            </div>

            {/* Display Loading spinner */}
            {(!TrackedCitationsIsLoaded || !TrackedMentionsIsLoaded) && (
              <div className="col-12 d-flex justify-content-center my-5">
                <Spinner />
              </div>
            )}

            {/* Warning for No Query Data */}
            {TrackedCitationsIsLoaded &&
              TrackedMentionsIsLoaded &&
              !hasQueryData && (
                <div className="col-12">
                  <div
                    className="alert alert-warning d-flex align-items-center"
                    role="alert"
                  >
                    <AlertCircle size={20} className="me-3 flex-shrink-0" />
                    <div className="flex-grow-1">
                      <strong>{t("tracking.noPromptsDataTitle", "No Prompts Data Available.")}</strong>{" "}
                      {t(
                        "tracking.noPromptsDataDesc",
                        "Add a prompt to start tracking, or change the selected engine using the dropdown in the Filter Dashboard above. You can also click the \"Add Prompt\" button above to add a prompt and begin tracking.",
                      )}
                    </div>
                  </div>
                </div>
              )}

            {/* Tracked Citations Table */}
            <div className="row align-items-stretch">
              {hasCitationData && (
                <div className="col-md-6 d-flex flex-column" data-tour="tracking.citations">
                  <NColumnTable
                    data={processedCitationData}
                    n={8}
                    columnKeys={citationColumnKeys}
                    columnTitles={citationColumnTitles}
                    columnRatio="30-5-11-11-11-11-11-10"
                    columnAlignment="l-c-r-r-r-r-r-r"
                    title={t("tracking.trackedCitations", "Tracked Citations")}
                    isLoaded={TrackedCitationsIsLoaded}
                    error={TrackedCitationsError}
                    boxHeight="300px"
                    wrapLength={200}
                    className="h-100"
                  />
                </div>
              )}

              {citation_valid_week_count >= TrackingWeekMinCount && (
                <div className="col-md-6 d-flex flex-column" data-tour="tracking.citation-trends">
                  <TrackingChart
                    data={TrackedCitationsResult?.tracking_result || []}
                    title={t("tracking.citationTrends", "Citation Trends")}
                    queryColumn={
                      TrackedCitationsResult?.column_names?.[0] || "query"
                    }
                    dateColumns={
                      TrackedCitationsResult?.column_names?.slice(1) || []
                    }
                    additionalInfo={
                      TrackedCitationsResult?.additional_info || null
                    }
                    isLoaded={TrackedCitationsIsLoaded}
                    error={TrackedCitationsError}
                    height={330}
                    className="h-100"
                  />
                </div>
              )}
            </div>

            <br />
            <br />

            <div className="row mt-3 align-items-stretch">
              {/* Tracked Mentions Table */}

              {hasMentionData && (
                <div className="col-md-6 d-flex flex-column" data-tour="tracking.mentions">
                  <NColumnTable
                    data={processedMentionData}
                    n={8}
                    columnKeys={mentionColumnKeys}
                    columnTitles={mentionColumnTitles}
                    columnRatio="30-5-11-11-11-11-11-10"
                    columnAlignment="l-c-r-r-r-r-r-r"
                    title={t("tracking.trackedMentions", "Tracked Mentions")}
                    isLoaded={TrackedMentionsIsLoaded}
                    error={TrackedMentionsError}
                    boxHeight="300px"
                    wrapLength={200}
                    className="h-100"
                  />
                </div>
              )}

              {mention_valid_week_count >= TrackingWeekMinCount && (
                <div className="col-md-6 d-flex flex-column">
                  <TrackingChart
                    data={TrackedMentionsResult?.tracking_result || []}
                    title={t("tracking.mentionTrends", "Mention Trends")}
                    queryColumn={
                      TrackedMentionsResult?.column_names?.[0] || "query"
                    }
                    dateColumns={
                      TrackedMentionsResult?.column_names?.slice(1) || []
                    }
                    additionalInfo={
                      TrackedMentionsResult?.additional_info || null
                    }
                    isLoaded={TrackedMentionsIsLoaded}
                    error={TrackedMentionsError}
                    height={330}
                    className="h-100"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Historical Queries Section */}
          {historicalQueries.length > 0 && TrackedCitationsIsLoaded && (
            <div className="mb-2 w-50" data-tour="tracking.searched-prompts">
              <SectionHeading className="mb-1 mt-3">
                {t("tracking.searchedPrompts", "Searched Prompts")}
              </SectionHeading>
              <p className="mt-1 fs-6 text-secondary mb-3">
                {t(
                  "tracking.searchedPromptsSubtitle",
                  "Add your searched prompts to track their performance over time",
                )}
              </p>
              <div>
                <ul>
                  {historicalQueries.slice(0, 5).map((query, index) => (
                    <li key={`historical-query-${index}-${query}`}>
                      <div className="mb-2">
                        <span className="text-dark">{query}</span>
                        <button
                          className="btn btn-sm btn-outline-light ms-2"
                          onClick={() => handleAddHistoricalQuery(query)}
                          disabled={addingHistoricalQuery === query}
                          style={{
                            borderColor: "var(--brand-color-dark)",
                            color: "var(--brand-color-dark)",
                          }}
                        >
                          {addingHistoricalQuery === query
                            ? t("common.adding", "Adding...")
                            : `+ ${t("tracking.track", "Track")}`}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Add URL Modal - Updated with date_tracked field */}
      {showAddUrlModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("tracking.addUrlTitle", "Add Page URL")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddUrlModal(false);
                    setNewUrl("");
                    setNewUrlDateTracked("");
                  }}
                  disabled={isSubmittingUrl}
                  aria-label={t("common.close", "Close")}
                ></button>
              </div>
              <form onSubmit={handleAddUrl}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="urlInput" className="form-label">
                      {t("common.url", "URL")} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      id="urlInput"
                      list="suggestedUrlsList"
                      placeholder={t(
                        "tracking.urlPlaceholder",
                        "https://example.com/page",
                      )}
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      required
                      disabled={isSubmittingUrl}
                    />
                    {SuggestedUrlsIsLoaded &&
                      SuggestedUrlsResult?.suggested_urls?.length > 0 && (
                        <datalist id="suggestedUrlsList">
                          {SuggestedUrlsResult.suggested_urls.map((url) => (
                            <option key={url} value={url}>
                              {cleanUrlForDisplay(url)}
                            </option>
                          ))}
                        </datalist>
                      )}
                    <small className="form-text text-muted">
                      {t(
                        "tracking.addUrlHint",
                        "Enter the complete URL you want to track, or choose from the suggested pages",
                      )}
                    </small>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="urlDateTracked" className="form-label">
                      {t("tracking.dateTracked", "Date Tracked")}{" "}
                      <span className="text-muted">
                        ({t("common.optional", "Optional")})
                      </span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="urlDateTracked"
                      value={newUrlDateTracked}
                      onChange={(e) => setNewUrlDateTracked(e.target.value)}
                      max={new Date().toISOString().split("T")[0]} // Can't select future dates
                      disabled={isSubmittingUrl}
                    />
                    <small className="form-text text-muted">
                      {t(
                        "tracking.dateTrackedHint",
                        "When did you start tracking this URL?",
                      )}
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddUrlModal(false);
                      setNewUrl("");
                      setNewUrlDateTracked("");
                    }}
                    disabled={isSubmittingUrl}
                  >
                    {t("common.cancel", "Cancel")}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{
                      backgroundColor: "var(--btn-solid-bg)",
                      color: "var(--btn-solid-text)",
                    }}
                    disabled={isSubmittingUrl}
                  >
                    {isSubmittingUrl ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t("common.adding", "Adding...")}
                      </>
                    ) : (
                      t("tracking.addPage", "Add Page")
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Query Modal - Updated with date_tracked field */}
      {showAddQueryModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("promptLibrary.addPrompt", "Add Prompt")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddQueryModal(false);
                    setNewQuery("");
                    setNewQueryDateTracked("");
                  }}
                  disabled={isSubmittingQuery}
                  aria-label={t("common.close", "Close")}
                ></button>
              </div>
              <form onSubmit={handleAddQuery}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="queryInput" className="form-label">
                      {t("promptLibrary.promptText", "Prompt")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="queryInput"
                      list="suggestedQueriesList"
                      placeholder={t(
                        "tracking.keywordPlaceholder",
                        "Enter search prompt",
                      )}
                      value={newQuery}
                      onChange={(e) => setNewQuery(e.target.value)}
                      required
                      disabled={isSubmittingQuery}
                    />
                    {SuggestedQueriesIsLoaded &&
                      SuggestedQueriesResult?.suggested_queries?.length > 0 && (
                        <datalist id="suggestedQueriesList">
                          {SuggestedQueriesResult.suggested_queries.map(
                            (query) => (
                              <option key={query} value={query} />
                            ),
                          )}
                        </datalist>
                      )}
                    <small className="form-text text-muted">
                      {t(
                        "tracking.addPromptHint",
                        "Enter the prompt you want to track, or choose from the suggested prompts",
                      )}
                    </small>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="queryDateTracked" className="form-label">
                      {t("tracking.dateTracked", "Date Tracked")}{" "}
                      <span className="text-muted">
                        ({t("common.optional", "Optional")})
                      </span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="queryDateTracked"
                      value={newQueryDateTracked}
                      onChange={(e) => setNewQueryDateTracked(e.target.value)}
                      max={new Date().toISOString().split("T")[0]} // Can't select future dates
                      disabled={isSubmittingQuery}
                    />
                    <small className="form-text text-muted">
                      {t(
                        "tracking.dateTrackedPromptHint",
                        "When did you start tracking this prompt?",
                      )}
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddQueryModal(false);
                      setNewQuery("");
                      setNewQueryDateTracked("");
                    }}
                    disabled={isSubmittingQuery}
                  >
                    {t("common.cancel", "Cancel")}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{
                      backgroundColor: "var(--btn-solid-bg)",
                      color: "var(--btn-solid-text)",
                    }}
                    disabled={isSubmittingQuery}
                  >
                    {isSubmittingQuery ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t("common.adding", "Adding...")}
                      </>
                    ) : (
                      t("promptLibrary.addPrompt", "Add Prompt")
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrackingPage;
